import ReactECharts from "echarts-for-react";
import React, { useMemo } from "react";
import styled from "styled-components";
import { currencySymbol } from "../../formatting/currency-utils";
import formatCurrency from "../../formatting/formatCurrency";
import { formatUnits } from "../../formatting/formatKiloUnits";
import Tooltip from "../Tooltip";
import {
  legendKeyColors,
  OperationalChartDataPoint,
  OperationalDataType,
} from "./chart-types";
import {
  ChartHeader,
  ChartLegendItem,
  ChartLegendItemColor,
  ChartLegendItemLabel,
  ChartLegendWrapper,
  ChartWrapper,
} from "./common";

interface OperationalSiteChartProps {
  header: string;
  chartData: OperationalChartDataPoint[];
  dataType: OperationalDataType;
  currencyCode?: string;
  toolTipText: string;
  height?: string;
  showBothForecastAndActual?: boolean;
}

interface SeriesDataPoint {
  monthIndex: string;
  value: number;
  metaFirstDay?: string;
  metaLastDay?: string;
  isActual: boolean;
}

interface ChartSeries {
  key: string;
  color: string;
  stack: string;
  data: SeriesDataPoint[];
}

const OperationalSiteChart: React.FC<OperationalSiteChartProps> = ({
  header,
  chartData,
  dataType,
  currencyCode,
  toolTipText,
  showBothForecastAndActual = false,
  ...props
}) => {
  const { series, xAxisData, legendItems } = useMemo(() => {
    const seriesMap = new Map<string, ChartSeries>();
    const xAxisData: string[] = [];

    for (const point of chartData) {
      const hasActual =
        point.actual && point.actual.some((item) => item.value !== 0);

      if (showBothForecastAndActual) {
        // Single x-axis entry per month - bars will be grouped together
        xAxisData.push(point.index);

        // Process actual data
        if (point.actual) {
          for (const item of point.actual) {
            if (!seriesMap.has(item.key)) {
              seriesMap.set(item.key, {
                key: item.key,
                color: legendKeyColors[item.key] || "#888888",
                stack: "", // No stacking - side by side bars
                data: [],
              });
            }
            seriesMap.get(item.key)!.data.push({
              monthIndex: point.index,
              value: item.value,
              metaFirstDay: point.meta_first_day,
              metaLastDay: point.meta_last_day,
              isActual: true,
            });
          }
        }

        // Process forecast data
        if (point.forecast) {
          for (const item of point.forecast) {
            if (!seriesMap.has(item.key)) {
              seriesMap.set(item.key, {
                key: item.key,
                color: legendKeyColors[item.key] || "#888888",
                stack: "", // No stacking - side by side bars
                data: [],
              });
            }
            seriesMap.get(item.key)!.data.push({
              monthIndex: point.index,
              value: item.value,
              isActual: false,
            });
          }
        }
      } else {
        // Single bar mode: prefer actual over forecast
        xAxisData.push(point.index);

        if (hasActual && point.actual) {
          for (const item of point.actual) {
            if (!seriesMap.has(item.key)) {
              seriesMap.set(item.key, {
                key: item.key,
                color: legendKeyColors[item.key] || "#888888",
                stack: "single",
                data: [],
              });
            }
            seriesMap.get(item.key)!.data.push({
              monthIndex: point.index,
              value: item.value,
              metaFirstDay: point.meta_first_day,
              metaLastDay: point.meta_last_day,
              isActual: true,
            });
          }
        } else if (point.forecast) {
          for (const item of point.forecast) {
            if (!seriesMap.has(item.key)) {
              seriesMap.set(item.key, {
                key: item.key,
                color: legendKeyColors[item.key] || "#888888",
                stack: "single",
                data: [],
              });
            }
            seriesMap.get(item.key)!.data.push({
              monthIndex: point.index,
              value: item.value,
              isActual: false,
            });
          }
        }
      }
    }

    // Convert to array and sort: actuals first, then forecasts
    const seriesArray = Array.from(seriesMap.values()).sort((a, b) => {
      const aIsForecast = a.key.startsWith("Forecast");
      const bIsForecast = b.key.startsWith("Forecast");
      if (aIsForecast === bIsForecast) return 0;
      return aIsForecast ? 1 : -1;
    });

    // Build legend items
    const legendItems = seriesArray.map((s) => ({
      key: s.key,
      color: s.color,
    }));

    return { series: seriesArray, xAxisData, legendItems };
  }, [chartData, showBothForecastAndActual]);

  const echartsOption = useMemo(() => {
    const formatValue = (value: number) => {
      if (dataType === "currency") {
        return formatCurrency(value, currencyCode, 2);
      }
      return formatUnits(value, "kWh", dataType === "energy" ? 3 : undefined);
    };

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "none",
        },
        textStyle: {
          fontSize: 16,
          color: "#00022F",
          fontWeight: "normal",
          fontFamily: '"Manrope", sans-serif',
        },
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return "";

          // Get the month label from the first param
          const axisValue = params[0].axisValue as string;
          const displayMonth = axisValue;

          // Find metadata from the first non-zero series
          let headerText = displayMonth;
          const firstParam = params.find((p) => p.value > 0);
          if (firstParam) {
            const seriesData = series.find(
              (s) => s.key === firstParam.seriesName
            );
            const dataPoint = seriesData?.data.find(
              (d) => d.monthIndex === axisValue
            );
            if (dataPoint?.metaFirstDay && dataPoint?.metaLastDay) {
              headerText = `${formatTooltipDate(dataPoint.metaFirstDay)} - ${formatTooltipDate(dataPoint.metaLastDay)}`;
            }
          }

          let tooltip = `<h3 style="font-weight: bold; margin: 8px 0;">${headerText}</h3>`;

          for (const param of params) {
            if (param.value === 0) continue;
            const color = param.color;
            const name = param.seriesName;
            const value = formatValue(param.value);
            tooltip += `<div style="display: flex; align-items: center; margin: 4px 0;"><span style="display:inline-block;margin-right:8px;width:8px;height:8px;background-color:${color};"></span>`;
            tooltip += `${name}:&nbsp;<strong>${value}</strong></div>`;
          }

          return tooltip;
        },
      },
      legend: {
        show: false, // We use custom legend
      },
      grid: {
        left: "70px",
        right: "20px",
        bottom: "40px",
        top: "10px",
        containLabel: false,
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        axisLabel: {
          fontSize: 11,
          color: "#999",
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: "#e0e0e0",
          },
        },
      },
      yAxis: {
        type: "value",
        name: dataType === "currency" ? currencySymbol(currencyCode!) : "kWh",
        nameLocation: "middle",
        nameGap: 50,
        nameTextStyle: {
          color: "#999",
        },
        axisLabel: {
          fontSize: 11,
          color: "#999",
        },
        splitLine: {
          lineStyle: {
            color: "#f0f0f0",
          },
        },
      },
      series: series.map((s) => ({
        name: s.key,
        type: "bar",
        stack: s.stack || undefined,
        data: xAxisData.map((xValue) => {
          const dataPoint = s.data.find((d) => d.monthIndex === xValue);
          return dataPoint?.value ?? 0;
        }),
        itemStyle: {
          color: s.color,
        },
        emphasis: {
          disabled: true,
        },
        barGap: "20%", // Small gap between bars in same category
        barCategoryGap: "30%", // Larger gap between categories (months)
      })),
    };
  }, [series, xAxisData, dataType, currencyCode]);

  return (
    <ChartWrapper height={props.height} $padding={"15px 0 0 0"}>
      <OperationalChartHeader>
        <ChartHeader>{header}</ChartHeader>
        <Tooltip text={toolTipText} position={"bottomLeft"} />
      </OperationalChartHeader>
      <ChartContent>
        <ChartLegendWrapper>
          {legendItems.map((item) => (
            <ChartLegendItem key={item.key}>
              <ChartLegendItemColor color={item.color} />
              <ChartLegendItemLabel>{item.key}</ChartLegendItemLabel>
            </ChartLegendItem>
          ))}
        </ChartLegendWrapper>
        <ReactECharts
          option={echartsOption}
          style={{ height: "calc(100% - 40px)", width: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge={true}
        />
      </ChartContent>
    </ChartWrapper>
  );
};

export default OperationalSiteChart;

const formatTooltipDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const OperationalChartHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-right: 16px;
`;

const ChartContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;
